import React from 'react';
import { CVData } from '../../types';
import { BaseTemplate } from './BaseTemplate';
import { generateTemplateColors } from '../../lib/colorUtils';

interface Template6Props {
  data: CVData;
  pageNumber?: number;
  customColor?: string;
}

export function Template6({ data, pageNumber = 1, customColor }: Template6Props) {
  // Usar el color personalizado si está disponible, o el color por defecto
  const baseColor = customColor || '#4B0082';
  
  console.log("Template6 - Color personalizado recibido:", customColor);
  console.log("Template6 - Color base utilizado:", baseColor);
  
  // Generar colores derivados basados en el color principal
  const colors = generateTemplateColors(baseColor);
  
  console.log("Template6 - Colores generados:", colors);

  // Verificamos si hay cursos disponibles
  const hasCourses = data.courses && data.courses.length > 0;
  
  // Verificamos si es una página de continuación
  const isContinuationPage = (data as any).isContinuationPage || false;
  
  // Verificamos si es una página de continuación específica del caso 3
  const isCase3ContinuationPage = (data as any).isCase3ContinuationPage || false;
  
  // Verificamos si es una página de continuación específica del caso 4
  const isCase4ContinuationPage = (data as any).isCase4ContinuationPage || false;
  
  // Verificamos si es la segunda página del caso 5 (3 experiencias)
  const isCase5SecondPage = (data as any).isCase5SecondPage || false;
  
  // Verificamos si es la tercera página del caso 5 (solo cursos)
  const isCase5ThirdPage = (data as any).isCase5ThirdPage || false;
  
  // Verificamos si es la segunda o tercera página del caso 6
  const isCase6SecondPage = (data as any).isCase6SecondPage || false;
  const isCase6ThirdPage = (data as any).isCase6ThirdPage || false;
  
  // Verificamos si es la segunda página del caso 7
  const isCase7SecondPage = (data as any).isCase7SecondPage || false;
  
  // Verificamos si es la tercera página del caso 7
  const isCase7ThirdPage = (data as any).isCase7ThirdPage || false;
  
  // Verificamos si es la segunda, tercera o cuarta página del caso 8
  const isCase8SecondPage = (data as any).isCase8SecondPage || false;
  const isCase8ThirdPage = (data as any).isCase8ThirdPage || false;
  const isCase8FourthPage = (data as any).isCase8FourthPage || false;
  
  // Verificamos si es una página de continuación con experiencia + cursos
  const isExperiencePlusCourses = (data as any).isExperiencePlusCourses || false;
  
  // Verificamos si es una página de continuación que incluye experiencias
  const isContinuationWithExperiences = (data as any).isContinuationWithExperiences || false;
  
  // Verificamos si es una página de sólo cursos
  const isOnlyCoursesPage = isCase5ThirdPage || isCase8FourthPage || (data as any).isOnlyCoursesPage || false;

  // Verificamos si es una página de continuación para cualquier caso
  const isAnyCaseContinuationPage = isCase3ContinuationPage || isCase4ContinuationPage || 
                                   isCase5SecondPage || isCase5ThirdPage || 
                                   isCase6SecondPage || isCase6ThirdPage || 
                                   isCase7SecondPage || isCase7ThirdPage ||
                                   isCase8SecondPage || isCase8ThirdPage || isCase8FourthPage;

  console.log("Template6 - Es continuación:", isContinuationPage);
  console.log("Template6 - Es página de solo cursos:", isOnlyCoursesPage);
  console.log("Template6 - Hay cursos:", hasCourses);
  console.log("Template6 - Cursos disponibles:", data.courses);

  // Crear estilos para el encabezado principal basados en el color personalizado
  const headerStyle = {
    background: `linear-gradient(135deg, ${baseColor}, ${colors.sidebarTo})`,
    position: 'relative' as 'relative',
    overflow: 'hidden' as 'hidden'
  };

  // Estilo para el efecto "futurista" del encabezado
  const headerEffectStyle = {
    position: 'absolute' as 'absolute',
    top: 0,
    right: 0,
    width: '70%',
    height: '100%',
    background: `linear-gradient(to right, transparent, ${colors.sidebarTo}60)`,
    opacity: 0.6,
    borderTopRightRadius: '5px',
    borderBottomRightRadius: '5px',
  };

  // Función para renderizar los cursos
  const renderCourses = () => {
    if (!data.courses || data.courses.length === 0) {
      console.log("Template6 - No hay cursos para renderizar");
      return null;
    }
    
    console.log("Template6 - Renderizando cursos:", data.courses.length);
    return (
      <div className="bg-white/80 backdrop-blur-sm p-4 border border-gray-100 shadow-sm">
        <h3 className="text-base font-bold mb-3" style={{ color: colors.accentColor }}>Cursos y Actividades Adicionales</h3>
        <div className="space-y-4">
          {data.courses.map((course, index) => (
            <div 
              key={`course-${index}`}
              className="relative pl-6"
            >
              <span 
                className="absolute left-0 top-2 w-2 h-2 rounded-full" 
                style={{ backgroundColor: colors.accentColor }}
              ></span>
              <h4 className="font-bold text-gray-800 text-sm">{course.title}</h4>
              <div className="flex items-center">
                <p className="text-xs" style={{ color: colors.accentColor }}>{course.institution}</p>
                <span className="text-xs mx-2" style={{ color: colors.accentColor }}>|</span>
                <p className="text-xs" style={{ color: colors.accentColor }}>{course.period}</p>
              </div>
              {course.description && (
                <p className="text-xs text-gray-600 mt-1">{course.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Si es una página solo de cursos, solo mostramos los cursos
  if (isOnlyCoursesPage && hasCourses) {
    console.log("Template6 - Renderizando SOLO cursos sin otro contenido");
    return (
      <BaseTemplate
        data={data}
        colors={colors}
        isTemplate1={false}
        renderMainContent={() => (
          <div className="py-6">
            {renderCourses()}
          </div>
        )}
      />
    );
  }
  
  // Si es una página de continuación con experiencias
  if (isAnyCaseContinuationPage && data.experience && data.experience.length > 0) {
    console.log("Template6 - Renderizando continuación con experiencia");
    
    // Determinar si esta página debe mostrar cursos
    const shouldShowCourses = 
      hasCourses && 
      (isCase3ContinuationPage || isCase4ContinuationPage || isCase6ThirdPage || isCase7ThirdPage) && 
      !(isCase5SecondPage || isCase6SecondPage || isCase7SecondPage || 
        isCase8SecondPage || isCase8ThirdPage);
    
    return (
      <BaseTemplate
        data={data}
        colors={colors}
        isTemplate1={false}
        renderMainContent={(experienceItems) => (
          <div className="space-y-4 pt-4">
            {/* Experiencia laboral (continuación) */}
            {experienceItems && React.Children.count(experienceItems) > 0 && (
              <div className="bg-white/80 backdrop-blur-sm p-4 border border-gray-100 shadow-sm">
                <h3 className="text-base font-bold mb-3" style={{ color: colors.accentColor }}>Experiencia Profesional (continuación)</h3>
                <div className="relative">
                  {/* Línea vertical que conecta todos los elementos */}
                  <div 
                    className="absolute left-2 top-0 bottom-0 w-0.5" 
                    style={{ backgroundColor: `${colors.accentColor}40` }}
                  ></div>
                  
                  {/* Contenedor para todas las experiencias */}
                  <div className="space-y-4 ml-10">
                    {React.Children.map(experienceItems, (item, index) => (
                      <div key={`exp-cont-${index}`} className="relative">
                        {/* Punto en la línea de tiempo */}
                        <div 
                          className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                          style={{ 
                            backgroundColor: 'white', 
                            borderColor: colors.accentColor 
                          }}
                        ></div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Cursos y Actividades (para casos específicos) */}
            {shouldShowCourses && renderCourses()}
            
            {/* Skills Section si es apropiado */}
            {isCase6ThirdPage || isCase7ThirdPage || isCase8FourthPage && (
              <div className="bg-white/80 backdrop-blur-sm p-4 border border-gray-100 shadow-sm mb-4">
                <h3 className="text-base font-bold mb-3" style={{ color: colors.accentColor }}>Habilidades</h3>
                <div className="flex flex-wrap gap-2">
                  {data.skills.map((skill, index) => (
                    <span
                      key={`skill-${index}`}
                      className="px-2 py-1 text-xs border"
                      style={{ 
                        backgroundColor: `${colors.accentColor}10`, 
                        color: colors.accentColor,
                        borderColor: `${colors.accentColor}30`
                      }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Languages Section si es apropiado */}
            {isCase6ThirdPage || isCase7ThirdPage || isCase8FourthPage && (
              <div className="bg-white/80 backdrop-blur-sm p-4 border border-gray-100 shadow-sm">
                <h3 className="text-base font-bold mb-3" style={{ color: colors.accentColor }}>Idiomas</h3>
                <div className="space-y-2">
                  {data.languages.map((lang, index) => (
                    <div key={`lang-${index}`} className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium text-sm">{lang.name}</span>
                      <span className="text-xs" style={{ color: colors.accentColor }}>{lang.level}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      />
    );
  }

  return (
    <BaseTemplate
      data={data}
      colors={colors}
      isTemplate1={false}
      renderMainContent={(experienceItems, pageNumber) => {
        console.log(`Template6 - Renderizando página ${pageNumber}`);
        console.log(`Template6 - Experiencias en página ${pageNumber}:`, React.Children.count(experienceItems));
        console.log(`Template6 - Cursos en página ${pageNumber}:`, data.courses?.length || 0);
        
        if (pageNumber === 2) {
          console.log("Template6 - Contenido detallado de cursos:", JSON.stringify(data.courses));
        }

        return (
          <div className="space-y-4">
            {pageNumber === 1 ? (
              <>
                {/* Header - Solo nombre y cargo con efecto futurista - SOLO EN PÁGINA 1 */}
                <div className="relative -mx-6 -mt-6 p-6 mb-6" style={headerStyle}>
                  <div style={headerEffectStyle}></div>
                  <h1 className="text-3xl font-bold mb-1 text-white relative z-10">{data.name}</h1>
                  <h2 className="text-lg font-bold text-white/90 relative z-10">{data.title}</h2>
                </div>

                {/* Contenido en estructura vertical con espaciado reducido */}
                <div className="space-y-4">
                  {/* Education Section */}
                  <div className="bg-white/80 backdrop-blur-sm p-4 border border-gray-100 shadow-sm">
                    <h3 className="text-base font-bold mb-3" style={{ color: colors.accentColor }}>Educación</h3>
                    <div className="relative">
                      {/* Línea vertical que conecta todos los elementos */}
                      <div 
                        className="absolute left-2 top-0 bottom-0 w-0.5" 
                        style={{ backgroundColor: `${colors.accentColor}40` }}
                      ></div>
                      
                      {/* Contenedor para todos los elementos de educación */}
                      <div className="space-y-3 ml-10">
                        {data.education.map((edu, index) => (
                          <div key={`edu-${index}`} className="relative">
                            {/* Punto en la línea de tiempo */}
                            <div 
                              className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                              style={{ 
                                backgroundColor: 'white', 
                                borderColor: colors.accentColor 
                              }}
                            ></div>
                            
                            {/* Contenido de educación */}
                            <div>
                              <h4 className="font-bold text-gray-800 text-sm">{edu.degree}</h4>
                              <p className="text-xs" style={{ color: colors.accentColor }}>{edu.institution}</p>
                              <p className="text-xs text-gray-500">{edu.period}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Experience Section - Primera parte */}
                  <div className="bg-white/80 backdrop-blur-sm p-4 border border-gray-100 shadow-sm">
                    <h3 className="text-base font-bold mb-3" style={{ color: colors.accentColor }}>Experiencia Profesional</h3>
                    <div className="relative">
                      {/* Línea vertical que conecta todos los elementos */}
                      <div 
                        className="absolute left-2 top-0 bottom-0 w-0.5" 
                        style={{ backgroundColor: `${colors.accentColor}40` }}
                      ></div>
                      
                      {/* Contenedor para todas las experiencias */}
                      <div className="space-y-4 ml-10">
                        {React.Children.map(experienceItems, (item, index) => (
                          <div key={`exp-${index}`} className="relative">
                            {/* Punto en la línea de tiempo */}
                            <div 
                              className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                              style={{ 
                                backgroundColor: 'white', 
                                borderColor: colors.accentColor 
                              }}
                            ></div>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Contenido para páginas secundarias (SIN encabezado) */}
                <div className="pt-3 space-y-4">
                  {/* Experience Section (continuación) - Solo si hay elementos */}
                  {experienceItems && React.Children.count(experienceItems) > 0 && (
                    <div className="bg-white/80 backdrop-blur-sm p-4 border border-gray-100 shadow-sm">
                      <h3 className="text-base font-bold mb-3" style={{ color: colors.accentColor }}>Experiencia Profesional (continuación)</h3>
                      <div className="relative">
                        {/* Línea vertical que conecta todos los elementos */}
                        <div 
                          className="absolute left-2 top-0 bottom-0 w-0.5" 
                          style={{ backgroundColor: `${colors.accentColor}40` }}
                        ></div>
                        
                        {/* Contenedor para todas las experiencias */}
                        <div className="space-y-4 ml-10">
                          {React.Children.map(experienceItems, (item, index) => (
                            <div key={`exp-cont-${index}`} className="relative">
                              {/* Punto en la línea de tiempo */}
                              <div 
                                className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                                style={{ 
                                  backgroundColor: 'white', 
                                  borderColor: colors.accentColor 
                                }}
                              ></div>
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Cursos y Actividades Adicionales */}
                  {hasCourses && !isCase5SecondPage && !isCase6SecondPage && !isCase7SecondPage && 
                   !isCase8SecondPage && !isCase8ThirdPage && renderCourses()}
                  
                  {/* Skills Section en página 2 */}
                  <div className="bg-white/80 backdrop-blur-sm p-4 border border-gray-100 shadow-sm mb-4">
                    <h3 className="text-base font-bold mb-3" style={{ color: colors.accentColor }}>Habilidades</h3>
                    <div className="flex flex-wrap gap-2">
                      {data.skills.map((skill, index) => (
                        <span
                          key={`skill-${index}`}
                          className="px-2 py-1 text-xs border"
                          style={{ 
                            backgroundColor: `${colors.accentColor}10`, 
                            color: colors.accentColor,
                            borderColor: `${colors.accentColor}30`
                          }}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Languages Section en página 2 */}
                  <div className="bg-white/80 backdrop-blur-sm p-4 border border-gray-100 shadow-sm">
                    <h3 className="text-base font-bold mb-3" style={{ color: colors.accentColor }}>Idiomas</h3>
                    <div className="space-y-2">
                      {data.languages.map((lang, index) => (
                        <div key={`lang-${index}`} className="flex justify-between items-center">
                          <span className="text-gray-700 font-medium text-sm">{lang.name}</span>
                          <span className="text-xs" style={{ color: colors.accentColor }}>{lang.level}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      }}
    />
  );
}
